import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import { useTableExpand } from "components/Report-generator/context/TableExpandContext";
import { useFormatNumericHook } from "hooks";
import type { DataTableCardData, ReportTableColumn, TableRow } from "store/types";

interface Props {
  data?: DataTableCardData;
  loading?: boolean;
  columnSettings?: ReportTableColumn[];
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
}

type Tier = "F" | "M" | "S";

const TIER_STYLES: Record<Tier, { font: number; headerFont: number; padV: number; padH: number }> = {
  F: { font: 13, headerFont: 11, padV: 10, padH: 10 },
  M: { font: 11, headerFont: 9, padV: 8, padH: 12 },
  S: { font: 9, headerFont: 8, padV: 6, padH: 9 }
};

const MIN_COL_WIDTH_PCT = 5;
const MIN_NAME_COL_WIDTH_PCT = 15;

function getTier(widthPercent: number): Tier {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

export const DataTableCard: React.FC<Props> = ({
  data,
  loading,
  columnSettings,
  columnWidths,
  onColumnWidthsChange
}) => {
  const { widthPercent, isEditMode } = useCardSize();
  const externalExpand = useTableExpand();
  const formatNumeric = useFormatNumericHook();
  const tableRef = React.useRef<HTMLTableElement>(null);

  const settingsMap = React.useMemo(
    () => new Map((columnSettings || []).map((col) => [col.aggregation, col])),
    [columnSettings]
  );

  const [dragWidths, setDragWidths] = React.useState<number[] | null>(null);

  // The table always renders fully expanded — groups can no longer be collapsed.
  // indexOffsets is still used so row keys stay stable across the continuation
  // chunks produced by the page-splitter.
  const indexOffsets = externalExpand?.indexOffsets;

  // Move "Total" row from any position to the end
  const orderedRows = React.useMemo(() => {
    if (!data) return [];
    const rows = data.data;
    const totalIdx = rows.findIndex((r) => r.title?.toLowerCase() === "total");
    if (totalIdx <= -1 || totalIdx === rows.length - 1) return rows;
    return [...rows.slice(0, totalIdx), ...rows.slice(totalIdx + 1), rows[totalIdx]];
  }, [data]);

  if (loading || !data) {
    return (
      <Box sx={{ p: "20px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const tier = getTier(widthPercent);
  const { font: bodyFont, headerFont, padV, padH } = TIER_STYLES[tier];

  // Fixed line heights and minimum cell heights per tier — keeps rendered row
  // heights deterministic and in sync with the estimation in estimateRowH.
  const singleLineH = Math.ceil(bodyFont * 1.5);
  const cellMinH = singleLineH + 2 * padV;
  const headerLineH = Math.ceil(headerFont * 1.5);
  const headerCellMinH = headerLineH + 2 * padV;

  const columns = data.columns.map((col) => (typeof col === "string" ? { key: col, title: col } : col));

  if (columns.length === 0) {
    return (
      <Box sx={{ p: "20px 16px" }}>
        {loading && <LinearProgress sx={{ mb: "4px" }} />}
        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No columns in the response</Typography>
      </Box>
    );
  }

  // Resolve column widths: use stored widths if they cover all columns, otherwise smart defaults
  const columnKeys = columns.map((col) => col.key);
  const colCount = columns.length;
  const minWidths = columns.map((_, i) => (i === 0 ? MIN_NAME_COL_WIDTH_PCT : MIN_COL_WIDTH_PCT));
  let resolvedWidths: number[];

  if (columnWidths && columnKeys.every((key) => columnWidths[key] != null)) {
    const raw = columnKeys.map((key) => columnWidths[key]);
    const sum = raw.reduce((a, b) => a + b, 0);
    resolvedWidths =
      sum > 0
        ? raw.map((w) => (w / sum) * 100)
        : columns.map((_, i) => (i === 0 ? MIN_NAME_COL_WIDTH_PCT : (100 - MIN_NAME_COL_WIDTH_PCT) / (colCount - 1)));
  } else if (colCount <= 2) {
    resolvedWidths = columns.map(() => 100 / colCount);
  } else {
    // Name column gets more space: ~25% or equal share, whichever is larger
    const nameWidth = Math.max(25, 100 / colCount);
    const dataWidth = (100 - nameWidth) / (colCount - 1);
    resolvedWidths = columns.map((_, i) => (i === 0 ? nameWidth : dataWidth));
  }

  const activeWidths = dragWidths ?? resolvedWidths;

  const canResize = isEditMode && !!onColumnWidthsChange;

  const handleResizePointerDown = (colIndex: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const tableEl = tableRef.current;
    if (!tableEl) return;
    const tableWidth = tableEl.getBoundingClientRect().width;
    const snapshot = [...resolvedWidths];

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const leftMin = minWidths[colIndex];
    const rightMin = minWidths[colIndex + 1];

    const onPointerMove = (me: PointerEvent) => {
      const deltaPx = me.clientX - startX;
      const deltaPct = (deltaPx / tableWidth) * 100;
      let leftW = snapshot[colIndex] + deltaPct;
      let rightW = snapshot[colIndex + 1] - deltaPct;

      if (leftW < leftMin) {
        leftW = leftMin;
        rightW = snapshot[colIndex] + snapshot[colIndex + 1] - leftMin;
      }
      if (rightW < rightMin) {
        rightW = rightMin;
        leftW = snapshot[colIndex] + snapshot[colIndex + 1] - rightMin;
      }

      const next = [...snapshot];
      next[colIndex] = leftW;
      next[colIndex + 1] = rightW;
      setDragWidths(next);
    };

    const onPointerUp = () => {
      setDragWidths((current) => {
        if (current && onColumnWidthsChange) {
          const widthMap: Record<string, number> = {};
          columnKeys.forEach((key, i) => {
            widthMap[key] = current[i];
          });
          onColumnWidthsChange(widthMap);
        }
        return null;
      });
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const formatValue = (value: unknown, colKey: string, colTitle?: string): string => {
    if (value === null || value === undefined) return "-";
    const settings = settingsMap.get(colKey) || (colTitle ? settingsMap.get(colTitle) : undefined);
    if (typeof value === "number" || typeof value === "string") {
      return formatNumeric({
        value,
        format: { abbreviated: settings?.abbreviated, decimal: settings?.decimal ?? 2 }
      });
    }
    return String(value);
  };

  const renderRow = (row: TableRow, rowIndex: number, depth: number, parentKey: string): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    const offset = indexOffsets?.[parentKey] ?? 0;
    const rowKey = `${parentKey}-${rowIndex + offset}`;
    const hasChildren = !!row.children?.length;
    const isGroup = hasChildren;
    const isTotalRow = depth === 0 && row.title?.toLowerCase() === "total";

    rows.push(
      <Box
        component="tr"
        key={rowKey}
        sx={{
          borderBottom: isTotalRow ? "none" : "1px solid #F3F4F6",
          ...(isTotalRow
            ? { borderTop: "2px solid #E5E7EB", bgcolor: "#F9FAFB" }
            : { bgcolor: isGroup ? "#F9FAFB" : rowIndex % 2 === 0 ? "#FFFFFF" : "#F9FAFB" })
        }}
      >
        {columns.map((col, colIndex) => {
          let cellContent: string;

          if (colIndex === 0) {
            cellContent = row.title || "";
          } else if (row.valueData) {
            cellContent = formatValue(row.valueData[col.key], col.aggregation || col.key, col.title);
          } else if (typeof row[col.key] !== "undefined") {
            cellContent = formatValue(row[col.key], col.aggregation || col.key, col.title);
          } else {
            cellContent = "-";
          }

          if (col.postfix && cellContent !== "-" && cellContent !== "") {
            cellContent = col.postfix === "%" ? `${cellContent}${col.postfix}` : `${col.postfix}${cellContent}`;
          }

          return (
            <Box
              component="td"
              key={col.key}
              sx={{
                p: `${padV}px ${padH}px`,
                textAlign: !colIndex ? "left" : "center",
                color: colIndex === 0 ? "#111827" : "#374151",
                fontWeight: isTotalRow || colIndex === 0 || isGroup ? (isTotalRow || isGroup ? 600 : 500) : 400,
                fontSize: bodyFont,
                lineHeight: 1.5,
                height: cellMinH,
                whiteSpace: colIndex === 0 ? "normal" : "nowrap",
                ...(colIndex === 0 ? { wordBreak: "normal" as const } : {}),
                ...(depth > 0 && colIndex === 0 ? { pl: `${padH + depth * 12}px` } : {})
              }}
            >
              {cellContent}
            </Box>
          );
        })}
      </Box>
    );

    if (hasChildren) {
      row.children!.forEach((child, childIndex) => {
        rows.push(...renderRow(child, childIndex, depth + 1, rowKey));
      });
    }

    return rows;
  };

  return (
    <Box data-table-card sx={{ overflowX: "hidden", p: "4px 0" }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}

      <Box
        component="table"
        ref={tableRef}
        sx={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          fontSize: bodyFont
        }}
      >
        <colgroup>
          {columns.map((col, i) => (
            <col key={col.key} style={{ width: `${activeWidths[i].toFixed(2)}%` }} />
          ))}
        </colgroup>

        <thead>
          <Box component="tr" sx={{ borderBottom: "2px solid #E5E7EB" }}>
            {columns.map((col, colIndex) => (
              <Box
                component="th"
                key={`${col.key}-${colIndex}`}
                sx={{
                  p: `${padV}px ${padH}px`,
                  textAlign: colIndex === 0 ? "left" : "center",
                  fontSize: headerFont,
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  lineHeight: 1.5,
                  height: headerCellMinH,
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  overflow: "hidden",
                  position: "relative"
                }}
              >
                {col.title}
                {canResize && colIndex < columns.length - 1 && (
                  <Box
                    onPointerDown={(e) => handleResizePointerDown(colIndex, e)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: -3,
                      bottom: 0,
                      width: 6,
                      cursor: "col-resize",
                      zIndex: 10,
                      "&:hover": { bgcolor: "rgba(26,60,52,0.18)" }
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </thead>

        <tbody>
          {orderedRows.map((row, rowIndex) => renderRow(row, rowIndex, 0, "root"))}

          {orderedRows.length === 0 && (
            <tr>
              <Box
                component="td"
                colSpan={columns.length}
                sx={{
                  p: "24px 16px",
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: 13
                }}
              >
                No data available
              </Box>
            </tr>
          )}
        </tbody>
      </Box>
    </Box>
  );
};
